<script setup>
/**
 * Popover — drop-in replacement for BV2's b-popover.
 *
 * Usage: wrap the trigger element in the default slot; put popover content in #content slot.
 *
 *   <Popover placement="bottom" trigger="click" custom-class="my-class">
 *       <button>Trigger</button>
 *       <template #content>Rich content here</template>
 *   </Popover>
 *
 * Props:
 *   customClass — extra class on the popover element
 *   placement   — 'top' | 'bottom' | 'left' | 'right' (default: 'top')
 *   trigger     — 'hover' | 'click' (default: 'hover')
 */
import { ref, nextTick, watch, onMounted, onBeforeUnmount } from 'vue';
import Popper from 'popper.js';

const emit = defineEmits(['click-outside']);

const props = defineProps({
    customClass: { type: String, default: '' },
    placement:   { type: String, default: 'top' },
    trigger:     { type: String, default: 'hover' },
    target:      { type: String, default: '' }, // element id to anchor Popper to (skips slot anchor)
    tooltip:     { type: Boolean, default: false },
});

const show     = ref(false);
const anchorEl = ref(null);
const contentEl = ref(null);
let   _popper  = null;
let   _docClickHandler = null;
let   _targetClickHandler = null;
let   _clickedTargetEl = null;

// For trigger="manual" and trigger="click" with external target: use a document listener
// to detect outside clicks. Added via nextTick to avoid capturing the opening click itself.
// For trigger="click" with target: also exclude the target element so its own click handler
// can toggle the popover without the doc listener closing it first.
watch(show, (val) => {
    const usesDocListener = props.trigger === 'manual' || (props.trigger === 'click' && props.target);
    if (!usesDocListener) return;
    if (val) {
        nextTick(() => {
            _docClickHandler = (e) => {
                const insideContent = contentEl.value && contentEl.value.contains(e.target);
                const insideTarget  = props.target && [...document.querySelectorAll('#' + props.target)].some(el => el.contains(e.target));
                if (!insideContent && !insideTarget) {
                    close();
                    if (props.trigger === 'manual') emit('click-outside', e);
                }
            };
            document.addEventListener('click', _docClickHandler);
        });
    } else {
        if (_docClickHandler) {
            document.removeEventListener('click', _docClickHandler);
            _docClickHandler = null;
        }
    }
});

function createPopper() {
    const anchor = _clickedTargetEl || (props.target ? document.getElementById(props.target) : anchorEl.value);
    if (!anchor || !contentEl.value) return;
    destroyPopper();
    _popper = new Popper(anchor, contentEl.value, {
        placement: props.placement,
        modifiers: {
            arrow: { element: '.arrow' },
            preventOverflow: { enabled: false },
            flip: { enabled: false },
        },
    });
}

function destroyPopper() {
    if (_popper) { _popper.destroy(); _popper = null; }
}

async function open() {
    show.value = true;
    await nextTick();
    createPopper();
}

function close() {
    show.value = false;
    destroyPopper();
}

function onEnter() { if (props.trigger === 'hover') open(); }
function onLeave() { if (props.trigger === 'hover') close(); }
function onClickTrigger() { if (props.trigger === 'click') show.value ? close() : open(); }
function onClickOutside() { if (props.trigger === 'click' && !props.target && show.value) close(); }

onMounted(() => {
    if (contentEl.value) document.body.appendChild(contentEl.value);

    if (props.target && props.trigger === 'click') {
        _targetClickHandler = (e) => {
            _clickedTargetEl = e.currentTarget;
            show.value ? close() : open();
        };
        document.querySelectorAll('#' + props.target).forEach(el => {
            el.addEventListener('click', _targetClickHandler);
        });
    }
});

defineExpose({ open, close });

onBeforeUnmount(() => {
    if (_docClickHandler) document.removeEventListener('click', _docClickHandler);
    if (_targetClickHandler) {
        document.querySelectorAll('#' + props.target).forEach(el => {
            el.removeEventListener('click', _targetClickHandler);
        });
    }
    destroyPopper();
    if (contentEl.value) contentEl.value.remove();
});
</script>

<template>
    <span
        ref="anchorEl"
        @mouseenter="onEnter"
        @mouseleave="onLeave"
        @click.stop="onClickTrigger"
        v-click-outside="onClickOutside"
    >
        <slot />

        <div
            v-show="show"
            ref="contentEl"
            role="tooltip"
            :class="tooltip
                ? ['tooltip', `bs-tooltip-${placement.split('-')[0]}`, customClass]
                : ['popover', `bs-popover-${placement.split('-')[0]}`, customClass]"
            style="z-index: 1060; max-width: 276px;"
        >
            <div class="arrow"></div>
            <div :class="tooltip ? 'tooltip-inner' : 'popover-body'">
                <slot name="content" />
            </div>
        </div>
    </span>
</template>

<style scoped>
.popover {
    text-align: left;
}
</style>
